import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LineWave } from "react-loader-spinner";
import { toast } from "react-toastify";
import ReactPaginate from "react-paginate";

import useDebounce from "../../../hooks/useDebounce";
import {
  clearSelectedCreator,
  fetchManualCreatorPayoutDetails,
  fetchManualCreatorPayouts,
  markManualCreatorPayoutPaid,
  payManualCreatorPayoutStripe,
} from "../../../redux/slices/manualPayoutsSlice";

function formatUsd(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "$0.00";
  return `$${num.toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

export default function ManualCreatorPayouts() {
  const dispatch = useDispatch();
  const {
    isLoading,
    isDetailLoading,
    isMarkingPaid,
    isPayingStripe,
    creators,
    meta,
    selectedCreator,
  } = useSelector((state) => state.manualPayouts);

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [showDetail, setShowDetail] = useState(false);
  const [showMarkPaid, setShowMarkPaid] = useState(false);
  const [showPayStripe, setShowPayStripe] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const payStripeInFlightRef = useRef(false);

  const debouncedSearch = useDebounce(query, 500);

  useEffect(() => {
    dispatch(
      fetchManualCreatorPayouts({
        search: debouncedSearch,
        page,
        limit: 20,
      })
    );
  }, [dispatch, debouncedSearch, page]);

  const remainingDefault = useMemo(() => {
    return selectedCreator?.balance?.remainingOwed ?? 0;
  }, [selectedCreator]);

  const openDetails = async (userId) => {
    const result = await dispatch(fetchManualCreatorPayoutDetails(userId));
    if (result?.payload?.code === 200) {
      setShowDetail(true);
      setPayAmount(
        String(result.payload?.data?.balance?.remainingOwed ?? "")
      );
    } else {
      toast.error(result?.payload?.message || "Failed to load details");
    }
  };

  const closeDetails = () => {
    setShowDetail(false);
    setShowMarkPaid(false);
    setShowPayStripe(false);
    setPaymentReference("");
    setAdminNote("");
    dispatch(clearSelectedCreator());
  };

  const refreshAfterPayout = async (result) => {
    if (!selectedCreator?.creator?.userId) return;

    await dispatch(
      fetchManualCreatorPayoutDetails(selectedCreator.creator.userId)
    );
    dispatch(
      fetchManualCreatorPayouts({
        search: debouncedSearch,
        page,
        limit: 20,
      })
    );

    const remaining = result?.payload?.data?.balance?.remainingOwed;
    if (Number(remaining) <= 0) {
      closeDetails();
    } else {
      setPayAmount(String(remaining));
    }
  };

  const handlePayStripe = async () => {
    if (!selectedCreator?.creator?.userId) return;
    if (payStripeInFlightRef.current) return;

    const amount = Number(payAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid payout amount");
      return;
    }

    const connectStatus =
      selectedCreator.paymentInfo?.connectOnboardingStatus || "";
    if (connectStatus !== "onBoarded") {
      toast.error("Creator must complete Stripe Connect onboarding first");
      return;
    }

    payStripeInFlightRef.current = true;
    try {
      const result = await dispatch(
        payManualCreatorPayoutStripe({
          userId: selectedCreator.creator.userId,
          amount,
          adminNote,
        })
      );

      if (result?.payload?.code === 200) {
        toast.success(
          result.payload.message ||
          "Legacy payout sent via Stripe (platform → bank)"
        );
        setShowPayStripe(false);
        setAdminNote("");
        await refreshAfterPayout(result);
      } else {
        toast.error(result?.payload?.message || "Stripe payout failed");
      }
    } finally {
      payStripeInFlightRef.current = false;
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedCreator?.creator?.userId) return;

    const amount = Number(payAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid payout amount");
      return;
    }

    const result = await dispatch(
      markManualCreatorPayoutPaid({
        userId: selectedCreator.creator.userId,
        amount,
        paymentReference,
        adminNote,
      })
    );

    if (result?.payload?.code === 200) {
      toast.success(result.payload.message || "Marked as paid");
      setShowMarkPaid(false);
      setPaymentReference("");
      setAdminNote("");
      await refreshAfterPayout(result);
    } else {
      toast.error(result?.payload?.message || "Failed to mark as paid");
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h1 className="text-left text-white text-3xl font-bold mb-2">
            Manual Creator Payouts
          </h1>
        </div>
        <button
          type="button"
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
          onClick={() =>
            dispatch(
              fetchManualCreatorPayouts({
                search: debouncedSearch,
                page,
                limit: 20,
              })
            )
          }
        >
          Refresh unpaid list
        </button>
      </div>

      <div className="mb-4 text-left">
        <input
          type="text"
          placeholder="Search by name, email, company, or userId"
          className="bg-[#372F5B] h-10 px-4 py-2 text-white rounded-full text-sm focus:outline-none w-full max-w-[420px]"
          value={query}
          onChange={(e) => {
            setPage(1);
            setQuery(e.target.value);
          }}
        />
      </div>

      <div className="overflow-x-auto bg-[#211849] rounded-xl border border-[#372F5B]">
        <table className="min-w-full text-sm text-left text-gray-200">
          <thead className="bg-[#150c3f] text-gray-300">
            <tr>
              <th className="px-4 py-3 text-center">Creator</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Platform sales</th>
              <th className="px-4 py-3">70% owed</th>
              <th className="px-4 py-3">Already paid / reserved</th>
              <th className="px-4 py-3">Remaining</th>
              <th className="px-4 py-3">Connect</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {creators?.length > 0 ? (
              creators.map((row) => (
                <tr
                  key={row.userId}
                  className="border-t border-[#372F5B] hover:bg-[#2a2055]"
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-white">
                      {row.name || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3">{row.email || "—"}</td>
                  <td className="px-4 py-3">
                    {formatUsd(row.platformSalesGross)}
                    <div className="text-xs text-gray-400">
                      {row.platformSaleCount} sale(s)
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {formatUsd(row.platformOwedSeventy)}
                  </td>
                  <td className="px-4 py-3">{formatUsd(row.manualPaid)}</td>
                  <td className="px-4 py-3 font-semibold text-amber-300">
                    {formatUsd(row.remainingOwed)}
                  </td>
                  <td className="px-4 py-3">
                    {row.connectOnboardingStatus || "none"}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className="bg-[#372F5B] hover:bg-[#4a3f75] text-white px-3 py-1.5 rounded-md text-xs"
                      onClick={() => openDetails(row.userId)}
                    >
                      View & pay
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-10 text-center text-gray-400"
                >
                  {isLoading
                    ? "Loading..."
                    : "No creators currently need manual payout."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {meta?.totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <ReactPaginate
            previousLabel="<"
            nextLabel=">"
            breakLabel="..."
            pageCount={meta.totalPages}
            forcePage={Math.max(0, (meta.currentPage || page) - 1)}
            onPageChange={(item) => setPage(item.selected + 1)}
            containerClassName="pagination flex gap-2 text-white"
            pageClassName="px-2"
            activeClassName="font-bold text-red-400"
          />
        </div>
      )}

      {isLoading && (
        <div className="loader">
          <LineWave color="#fff" loading={isLoading} size={150} />
        </div>
      )}

      {showDetail && selectedCreator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-[#211849] text-white w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-xl border border-[#372F5B] p-5">
            <div className="grid grid-cols-3 items-center gap-3 mb-4">
              <div />
              <div className="text-center">
                <h2 className="text-2xl font-bold">
                  {selectedCreator.creator?.name || "Creator"}
                </h2>
                <p className="text-sm text-gray-300">
                  {selectedCreator.creator?.email}
                </p>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  className="text-gray-300 hover:text-white text-xl"
                  onClick={closeDetails}
                >
                  ×
                </button>
              </div>
            </div>

            {isDetailLoading ? (
              <div className="py-10 flex justify-center">
                <LineWave color="#fff" loading size={120} />
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 gap-4 mb-5">
                  <div className="bg-[#150c3f] rounded-lg p-4 border border-[#372F5B]">
                    <h3 className="font-semibold mb-2">Payment info</h3>
                    <p className="text-sm">
                      Email: {selectedCreator.paymentInfo?.email || "—"}
                    </p>
                    <p className="text-sm">
                      Connect:{" "}
                      {selectedCreator.paymentInfo?.connectAccountId || "—"} (
                      {selectedCreator.paymentInfo?.connectOnboardingStatus ||
                        "none"}
                      )
                    </p>
                  </div>
                  <div className="bg-[#150c3f] rounded-lg p-4 border border-[#372F5B]">
                    <h3 className="font-semibold mb-2">Balance</h3>
                    <p className="text-sm">
                      Platform sales:{" "}
                      {formatUsd(selectedCreator.balance?.platformSalesGross)}
                    </p>
                    <p className="text-sm">
                      70% owed:{" "}
                      {formatUsd(selectedCreator.balance?.platformOwedSeventy)}
                    </p>
                    <p className="text-sm">
                      Already paid / reserved:{" "}
                      {formatUsd(selectedCreator.balance?.manualPaid)}
                    </p>
                    <p className="text-lg font-bold text-amber-300 mt-2">
                      Remaining:{" "}
                      {formatUsd(selectedCreator.balance?.remainingOwed)}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                        disabled={
                          Number(remainingDefault) <= 0 ||
                          selectedCreator.paymentInfo?.connectOnboardingStatus !== "onBoarded"
                        }
                        onClick={() => {
                          setPayAmount(String(remainingDefault));
                          setShowPayStripe(true);
                        }}
                      >
                        Pay via Stripe
                      </button>
                      <button
                        type="button"
                        className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                        disabled={Number(remainingDefault) <= 0}
                        onClick={() => {
                          setPayAmount(String(remainingDefault));
                          setShowMarkPaid(true);
                        }}
                      >
                        Record paid (offline)
                      </button>
                    </div>
                  </div>
                </div>

                <h3 className="font-semibold mb-2">
                  Platform-held transactions
                </h3>
                <div className="overflow-x-auto mb-5 border border-[#372F5B] rounded-lg">
                  <table className="min-w-full text-xs">
                    <thead className="bg-[#150c3f]">
                      <tr>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Order</th>
                        <th className="px-3 py-2 text-left">Book</th>
                        <th className="px-3 py-2 text-left">Book ID</th>
                        <th className="px-3 py-2 text-left">Sale</th>
                        <th className="px-3 py-2 text-left">Creator 70%</th>
                        <th className="px-3 py-2 text-left">Split</th>
                        <th className="px-3 py-2 text-left">PI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedCreator.transactions || []).length > 0 ? (
                        selectedCreator.transactions.map((txn) => (
                          <tr
                            key={txn._id}
                            className="border-t border-[#372F5B]"
                          >
                            <td className="px-3 py-2">
                              {formatDate(txn.createdAt)}
                            </td>
                            <td className="px-3 py-2">{txn.orderId ?? "—"}</td>
                            <td className="px-3 py-2">
                              {txn.bookTitle || "—"}
                            </td>
                            <td className="px-3 py-2 break-all font-mono text-[10px]">
                              {txn.bookId || "—"}
                            </td>
                            <td className="px-3 py-2">
                              {formatUsd(txn.saleAmount)}
                            </td>
                            <td className="px-3 py-2">
                              {formatUsd(txn.creatorShare)}
                            </td>
                            <td className="px-3 py-2">{txn.paymentSplit}</td>
                            <td className="px-3 py-2 break-all">
                              {txn.paymentIntentId || "—"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={8}
                            className="px-3 py-4 text-center text-gray-400"
                          >
                            No platform-held transactions
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold mb-2">Payout history</h3>
                <div className="overflow-x-auto border border-[#372F5B] rounded-lg">
                  <table className="min-w-full text-xs">
                    <thead className="bg-[#150c3f]">
                      <tr>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Amount</th>
                        <th className="px-3 py-2 text-left">Source</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Payout ID</th>
                        <th className="px-3 py-2 text-left">Reference</th>
                        <th className="px-3 py-2 text-left">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedCreator.manualPayoutHistory || []).length >
                        0 ? (
                        selectedCreator.manualPayoutHistory.map((p) => (
                          <tr key={p._id} className="border-t border-[#372F5B]">
                            <td className="px-3 py-2">
                              {formatDate(p.createdAt)}
                            </td>
                            <td className="px-3 py-2">
                              {formatUsd(p.amount)}
                            </td>
                            <td className="px-3 py-2">{p.source || "—"}</td>
                            <td className="px-3 py-2">
                              {p.transferToBankAccount || "—"}
                            </td>
                            <td className="px-3 py-2 break-all">
                              {p.payoutId || "—"}
                            </td>
                            <td className="px-3 py-2">
                              {p.paymentReference || "—"}
                            </td>
                            <td className="px-3 py-2">{p.adminNote || "—"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-3 py-4 text-center text-gray-400"
                          >
                            No payout records yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showPayStripe && selectedCreator && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#211849] w-full max-w-md rounded-xl border border-[#372F5B] p-5 text-white">
            <h3 className="text-xl font-bold mb-3">Pay legacy balance via Stripe</h3>
            <p className="text-sm text-gray-300 mb-4">
              Sends from the platform (main) account → creator Connect → bank.
              Remaining legacy owed: {formatUsd(remainingDefault)}.
            </p>
            <label className="block text-xs mb-1">Amount (USD)</label>
            <input
              min="0.01"
              step="0.01"
              className="w-full mb-3 bg-[#372F5B] rounded-lg px-3 py-2 text-sm cursor-default focus:outline-none"
              value={payAmount}
              readOnly
            />
            <label className="block text-xs mb-1">Admin note (optional)</label>
            <textarea
              className="w-full mb-4 bg-[#372F5B] rounded-lg px-3 py-2 text-sm focus:outline-none min-h-[80px]"
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-[#372F5B]"
                onClick={() => setShowPayStripe(false)}
                disabled={isPayingStripe}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-emerald-600 enabled:hover:bg-emerald-700 disabled:bg-emerald-700 disabled:opacity-50"
                onClick={handlePayStripe}
                disabled={isPayingStripe}
              >
                {isPayingStripe ? "Sending..." : "Confirm Stripe payout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMarkPaid && selectedCreator && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#211849] w-full max-w-md rounded-xl border border-[#372F5B] p-5 text-white">
            <h3 className="text-xl font-bold mb-3">Record offline payout</h3>
            <p className="text-sm text-gray-300 mb-4">
              Remaining unpaid: {formatUsd(remainingDefault)}. Use this only if
              you already paid outside Sidekick (check, wire, Stripe Dashboard)
              and need to update the ledger.
            </p>
            <label className="block text-xs mb-1">Amount (USD)</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              className="w-full mb-3 bg-[#372F5B] rounded-lg px-3 py-2 text-sm focus:outline-none"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
            />
            <label className="block text-xs mb-1">
              Payment reference (optional)
            </label>
            <input
              type="text"
              className="w-full mb-3 bg-[#372F5B] rounded-lg px-3 py-2 text-sm focus:outline-none"
              placeholder="Bank transfer ID / check # / etc."
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
            />
            <label className="block text-xs mb-1">Admin note (optional)</label>
            <textarea
              className="w-full mb-4 bg-[#372F5B] rounded-lg px-3 py-2 text-sm focus:outline-none min-h-[80px]"
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-[#372F5B]"
                onClick={() => setShowMarkPaid(false)}
                disabled={isMarkingPaid}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50"
                onClick={handleMarkPaid}
                disabled={isMarkingPaid}
              >
                {isMarkingPaid ? "Saving..." : "Confirm paid"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
